import { useRef, useState } from 'react';
import { AdminVideo } from '../common/model/dto/video/admin-video';
import {
  ReplacementFileRejection,
  StagedVideo,
} from '../common/model/type/video-file.types';
import {
  contentTypeForFile,
  readVideoDimensions,
  readVideoThumbnail,
  toCreateVideoRequest,
  validateVideoFile,
} from '../common/utils/video-file-utils';
import { NEW_PLACEMENT_VALUES } from '../common/utils/video-placement-utils';

export function useReplacementStaging(video: AdminVideo) {
  const [staged, setStaged] = useState<StagedVideo | null>(null);
  const [rejection, setRejection] = useState<ReplacementFileRejection | null>(
    null
  );
  const nextKey = useRef(0);

  const update = (changes: Partial<StagedVideo>) =>
    setStaged((current) => current && { ...current, ...changes });

  const updateIfCurrent = (key: string, changes: Partial<StagedVideo>) =>
    setStaged((current) =>
      current?.key === key ? { ...current, ...changes } : current
    );

  const choose = (files: File[]) => {
    if (files.length > 1) {
      setRejection({ kind: 'multiple' });

      return;
    }

    const [file] = files;

    if (!file) {
      return;
    }

    const contentType = contentTypeForFile(file);
    const reason = validateVideoFile(file);

    if (reason || !contentType) {
      setRejection({
        kind: 'file',
        name: file.name,
        reason: reason ?? 'unsupportedType',
      });

      return;
    }

    const key = String(nextKey.current++);
    setRejection(null);
    setStaged({
      key,
      file,
      contentType,
      title: video.title,
      description: video.description ?? '',
      dimensions: 'reading',
      thumbnail: 'reading',
      orientation: null,
      placement: NEW_PLACEMENT_VALUES,
    });
    readVideoDimensions(file).then((dimensions) =>
      updateIfCurrent(key, { dimensions })
    );
    readVideoThumbnail(file).then((thumbnail) =>
      updateIfCurrent(key, { thumbnail })
    );
  };

  const clear = () => {
    setStaged(null);
    setRejection(null);
  };

  return {
    staged,
    rejection,
    request: staged ? toCreateVideoRequest(staged) : null,
    choose,
    update,
    clear,
  };
}
